import React from "react"
import { useTranslation } from "react-i18next"

import { NzDataGrid } from "@/components/nzDataGrid"
import type { Meter } from "@/models/meter"
import type { Organization } from "@/models/organization"
import type { Site } from "@/models/site"
import { DEFAULT_PAGE_SIZE, useInfiniteScroll } from "@/nzds/data-grid"
import {
  INTERVAL_USAGE_LIST_FILTERS,
  type IntervalGridRecord,
  defaultIntervalRecordsSortModel,
  defaultIntervalRecordsSortParam,
  intervalRecordsGridFieldToSortParam,
  intervalToGridRecord,
  toIntervalMeterIdentity,
  toMeterIdsQueryParam,
  useIntervalsInfinite,
} from "@/services/interval"
import { parseModelId } from "@/utils/parse-model-id"

import { Box, Stack, Typography } from "@mui/material"
import type {
  GridSlotProps,
  GridSlots,
  GridSortModel,
} from "@mui/x-data-grid-pro"

import { IntervalDataRecordsGridToolbar } from "./interval-data-records-grid-toolbar"
import { buildIntervalRecordsExportPath } from "./interval-records-export-path"
import { isIntervalVisualizationLoading } from "./is-interval-visualization-loading"
import { useIntervalRecordsGridColumns } from "./use-interval-records-grid-columns"

const GRID_HEIGHT = 400

interface IntervalDataRecordsGridProps {
  hasReadyScope: boolean
  isLoading: boolean
  meters: Meter[]
  organizationId: Organization["id"]
  rangeEnd: string | undefined
  rangeIsValid: boolean
  rangeStart: string | undefined
  showFlowDirection: boolean
  siteId: Site["id"]
  timeZone: string
}

const getIntervalRecordRowId = (row: IntervalGridRecord): string =>
  `${row.meter_id}-${row.start_datetime}-${row.end_datetime}-${row.measurement_type}-${row.flow_direction}`

const nextIntervalRecordsSortModel = (
  sortModel: GridSortModel
): GridSortModel =>
  sortModel.length > 0 ? sortModel : defaultIntervalRecordsSortModel

interface ExportableIntervalRecordsCheck {
  downloadHref: string | undefined
  isError: boolean
  isQueryLoading: boolean
  total: number
}

const hasExportableIntervalRecords = ({
  downloadHref,
  isError,
  isQueryLoading,
  total,
}: ExportableIntervalRecordsCheck): boolean =>
  Boolean(downloadHref) && !isQueryLoading && !isError && total > 0

const resetGridVirtualScroller = (gridRoot: HTMLDivElement | null): void => {
  const virtualScroller = gridRoot?.querySelector(
    ".MuiDataGrid-virtualScroller"
  )
  if (virtualScroller instanceof HTMLElement) {
    virtualScroller.scrollTop = 0
  }
}

export const IntervalDataRecordsGrid: React.FC<
  IntervalDataRecordsGridProps
> = ({
  hasReadyScope,
  isLoading,
  meters,
  organizationId,
  rangeEnd,
  rangeIsValid,
  rangeStart,
  showFlowDirection,
  siteId,
  timeZone,
}) => {
  const { t } = useTranslation("sites")
  const gridRootRef = React.useRef<HTMLDivElement | null>(null)
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    defaultIntervalRecordsSortModel
  )

  const firstSort = sortModel[0]
  const sortParam =
    intervalRecordsGridFieldToSortParam(firstSort?.field, firstSort?.sort) ??
    defaultIntervalRecordsSortParam
  const meterIds = toMeterIdsQueryParam(meters)
  const columns = useIntervalRecordsGridColumns({
    showFlowDirection,
    timeZone,
  })
  const siteIdNumber = parseModelId(siteId)
  const isQueryEnabled =
    Boolean(siteIdNumber) && meterIds.length > 0 && rangeIsValid
  const listQuerySignature = JSON.stringify({
    meterIds,
    rangeEnd,
    rangeStart,
    sortParam,
  })

  const meterLookupById = new Map(
    meters.map((meter) => {
      const identity = toIntervalMeterIdentity(meter)
      return [identity.id, identity]
    })
  )

  const {
    intervalsData,
    intervalsFetchNextPage,
    intervalsHasNextPage,
    intervalsIsError,
    intervalsIsFetching,
    intervalsIsFetchingNextPage,
    intervalsIsLoading,
    intervalsIsPlaceholderData,
  } = useIntervalsInfinite({
    apiQueryOptions: {
      ...INTERVAL_USAGE_LIST_FILTERS,
      end: rangeEnd,
      limit: DEFAULT_PAGE_SIZE,
      meter_ids: meterIds,
      site_id: siteIdNumber,
      sort: sortParam,
      start: rangeStart,
    },
    organizationId,
    queryOptions: {
      enabled: isQueryEnabled,
      retry: false,
    },
  })

  const rows = (intervalsData?.pages ?? [])
    .flatMap((page) => page.items)
    .map((item) => intervalToGridRecord(item, meterLookupById))

  const { handleOnRowsScrollEnd } = useInfiniteScroll({
    fetchNextPage: intervalsFetchNextPage,
    hasNextPage: intervalsHasNextPage,
    isEnabled: isQueryEnabled,
    isFetchingNextPage: intervalsIsFetchingNextPage,
  })

  const previousListQuerySignatureRef = React.useRef(listQuerySignature)

  React.useEffect(() => {
    if (previousListQuerySignatureRef.current === listQuerySignature) {
      return
    }

    previousListQuerySignatureRef.current = listQuerySignature
    resetGridVirtualScroller(gridRootRef.current)
  }, [listQuerySignature])

  const isRecordsVisualizationLoading = isIntervalVisualizationLoading({
    hasReadyScope,
    isQueryEnabled,
    isQueryLoading: intervalsIsLoading,
    isScopeLoading: isLoading,
    rangeIsValid,
  })
  const isRecordsQueryLoading =
    isRecordsVisualizationLoading || intervalsIsPlaceholderData
  const downloadHref = buildIntervalRecordsExportPath(organizationId, {
    end: rangeEnd,
    meterIds,
    siteId,
    sort: sortParam,
    start: rangeStart,
  })
  const excelDownloadHref = buildIntervalRecordsExportPath(organizationId, {
    end: rangeEnd,
    format: "xlsx",
    meterIds,
    siteId,
    sort: sortParam,
    start: rangeStart,
  })
  const recordsTotal = intervalsData?.pages[0]?.total ?? 0
  const canDownload = hasExportableIntervalRecords({
    downloadHref,
    isError: intervalsIsError,
    isQueryLoading: isRecordsQueryLoading,
    total: recordsTotal,
  })

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Typography component="h3">{t("intervalData.grid.ariaLabel")}</Typography>
      <Box ref={gridRootRef} sx={{ height: GRID_HEIGHT }}>
        <NzDataGrid
          aria-label={t("intervalData.grid.ariaLabel")}
          columns={columns}
          disableColumnFilter
          disableColumnSelector
          getRowId={(row: IntervalGridRecord) => getIntervalRecordRowId(row)}
          loading={isRecordsVisualizationLoading || intervalsIsFetching}
          onRowsScrollEnd={handleOnRowsScrollEnd}
          onSortModelChange={(nextSortModel) => {
            setSortModel(nextIntervalRecordsSortModel(nextSortModel))
          }}
          rows={rows}
          slotProps={{
            toolbar: {
              canDownload,
              downloadHref,
              excelDownloadHref,
              isRecordCountLoading:
                intervalsIsFetching || intervalsIsPlaceholderData,
              recordCount: intervalsData?.pages[0]?.total,
            } as GridSlotProps["toolbar"],
          }}
          slots={{
            toolbar: IntervalDataRecordsGridToolbar as GridSlots["toolbar"],
          }}
          sortModel={sortModel}
          sortingMode="server"
        />
      </Box>
    </Stack>
  )
}
